import { resolveSeatLicenseType } from './portalOrganization.js'
import {
  inProgressSeatUpgradeFeePerSeat as inProgressSeatUpgradeFeePerSeatForQualification,
} from './portalStatePartnerPricing.js'
import {
  learnerEnrollmentLicenseMatchesOrganization,
  resolvedSeatLicenseTypeFromEnrollment,
} from '@/utils/courseLicenseAccess.js'

/** Org must be Premium / Supp. Premium before individual in-progress seat upgrades are allowed. */
export function canOrgUpgradeInProgressSeats(orgSeatLicenseType) {
  const type = resolveSeatLicenseType(orgSeatLicenseType)
  return type === 'Premium' || type === 'Supp. Premium'
}

/** @deprecated Prefer inProgressSeatUpgradeFeePerSeat from portalStatePartnerPricing.js */
export function inProgressSeatUpgradeFeePerSeat(orgSeatLicenseType, qualifies = false) {
  return inProgressSeatUpgradeFeePerSeatForQualification(orgSeatLicenseType, qualifies)
}

/** Source license for a seat being upgraded while the org stays on Premium / Supp. Premium. */
export function inferFromLicenseForInProgressUpgrade(seatLicenseType, orgSeatLicenseType) {
  const resolved = seatLicenseType
    ? resolveSeatLicenseType(seatLicenseType)
    : null
  const orgType = resolveSeatLicenseType(orgSeatLicenseType)
  if (resolved && resolved !== orgType) return resolved
  if (orgType === 'Premium') return 'Basic'
  if (orgType === 'Supp. Premium') return 'Supp. Basic'
  return resolved ?? 'Basic'
}

/**
 * @param {{ seatLicenseType?: string | null, licenseBatch?: unknown }} row
 * @param {{ seatLicenseType?: string } | null | undefined} org
 */
export function isLearnerRowUpgradeEligible(row, org) {
  if (!canOrgUpgradeInProgressSeats(org?.seatLicenseType)) return false
  const enrollment = {
    seatLicenseType: row?.seatLicenseType,
    licenseBatch: row?.licenseBatch,
  }
  return !learnerEnrollmentLicenseMatchesOrganization(enrollment, org)
}

/**
 * @param {Record<string, unknown>} row
 * @param {string} orgSeatLicenseType
 */
export function serializeInProgressUpgradeSeatRow(row, orgSeatLicenseType) {
  const fromLicense = inferFromLicenseForInProgressUpgrade(
    row.seatLicenseType,
    orgSeatLicenseType
  )
  const toLicense = resolveSeatLicenseType(orgSeatLicenseType)
  return {
    id: row.id,
    studentId: row.studentId,
    courseId: row.courseId ?? null,
    name: row.name,
    course: row.course,
    contentType: row.contentType ?? 'course',
    fromLicense,
    toLicense,
    currentSeatLicense: resolvedSeatLicenseTypeFromEnrollment({
      seatLicenseType: row.seatLicenseType,
      licenseBatch: row.licenseBatch,
    }),
    progress: row.progress ?? null,
    enrolledDate: row.enrolledDate ?? null,
  }
}

/**
 * @param {Record<string, unknown>} s Portal snapshot state
 */
export function buildInProgressSeatUpgradeSelection(s) {
  const orgType = s.seatLicenseType
  const seatType = s.isSupplemental ? 'pov' : 'course'
  const upgradeable = (s.upgradeableActiveLearners ?? []).filter(
    (row) => (row.contentType ?? 'course') === seatType
  )
  const excludedIds = new Set(
    (s.excludedFromInProgressUpgrade ?? []).map((e) => e.id)
  )
  const included = upgradeable
    .filter((row) => !excludedIds.has(row.id))
    .map((row) => serializeInProgressUpgradeSeatRow(row, orgType))
  const excluded = upgradeable
    .filter((row) => excludedIds.has(row.id))
    .map((row) => ({
      ...serializeInProgressUpgradeSeatRow(row, orgType),
      excludedAt: s.excludedFromInProgressUpgrade?.find((e) => e.id === row.id)
        ?.excludedAt,
    }))
  return {
    orgSeatLicenseType: orgType,
    // List (pre-discount) fee — matches line-item unitPrice; partner savings use discount fields.
    feePerSeat:
      Number(s.listInProgressUpgradeFeePerSeat) ||
      inProgressSeatUpgradeFeePerSeat(orgType, false),
    includedSeats: included,
    excludedSeats: excluded,
    includedCount: included.length,
    excludedCount: excluded.length,
  }
}
